import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CallWithAuth } from '../../../actions/apiAction';
import { CREATOR_BANK_ACCOUNT } from '../../../utils/apiUrl';
import { PAYMENT_SETUP_ENABLED } from '../../../utils/featureFlags';
import {
    getEarningsMetrics,
    peekEarningsMetrics,
} from '../../../utils/earningsCache';

const formatMoney = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '0.00';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCalendarDate = (isoDate) => {
    if (!isoDate) return null;
    const [year, month, day] = String(isoDate).split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
    });
};

export default function PayoutHistory() {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(() => peekEarningsMetrics());
    const [bankStatus, setBankStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);

    const load = useCallback(async () => {
        setStatusLoading(true);
        try {
            const earnings = await getEarningsMetrics();
            if (earnings) setSummary(earnings);
            if (PAYMENT_SETUP_ENABLED) {
                const statusRes = await CallWithAuth('GET', CREATOR_BANK_ACCOUNT);
                if (statusRes?.data) setBankStatus(statusRes.data);
            }
        } catch (err) {
            console.error('Failed to load payout history', err);
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const payoutReady = Boolean(bankStatus?.payout_ready);

    return (
        <>
            <Helmet>
                <title>Payout History</title>
            </Helmet>

            <div className="main-body">
                <div className="main-header-s1 center-text">
                    <button type="button" className="back-btn" onClick={() => navigate(-1)}>
                        <img src="/images/icons/chevron-left.svg" alt="" />
                    </button>
                    <h1>Payout History</h1>
                    <button type="button" className="transparent-btn">
                        <img src="/images/icons/info.svg" alt="" />
                    </button>
                </div>
                <div className="body-content">
                    <div className="my-subscribers-layout">
                        {statusLoading && !summary ? (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div
                                    className="layout-box widget-type2"
                                    style={{
                                        minHeight: 220,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div
                                        className="w-10 h-10 rounded-full border-4 border-transparent border-t-[#AF0056] border-r-pink-400 animate-spin"
                                        aria-label="Loading"
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                        <div className="layout-item">
                            <div className="payoutHistory-information-card">
                                <div className="top-info">
                                    <h3>Available to pay</h3>
                                    <h2>${formatMoney(summary?.available_to_pay ?? summary?.available_balance)}</h2>
                                    <p className="balance-caption">
                                        ${formatMoney(summary?.held_balance)} held
                                        {formatCalendarDate(summary?.next_available_date)
                                            ? ` until ${formatCalendarDate(summary.next_available_date)}`
                                            : ' through next month'}
                                    </p>
                                </div>
                                <div className="bottom-info">
                                    <h5>Lifetime Earnings</h5>
                                    <h4>${formatMoney(summary?.lifetime_earnings)}</h4>
                                </div>
                            </div>
                        </div>

                        {summary?.released_balance > 0 ? (
                            <div className="layout-item">
                                <div className="last-payout-card">
                                    <div className="last-payout-card__header">
                                        <h3>Paid out</h3>
                                        <span className="last-payout-card__status status-paid">
                                            Paid
                                        </span>
                                    </div>
                                    <div className="last-payout-card__row">
                                        <span className="last-payout-card__label">Amount</span>
                                        <span className="last-payout-card__value">
                                            ${formatMoney(summary.released_balance)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : summary ? (
                            <div className="layout-item">
                                <div className="last-payout-card last-payout-card--empty">
                                    <div className="last-payout-card__header">
                                        <h3>Last payout</h3>
                                    </div>
                                    <p className="last-payout-card__empty-text">
                                        No payouts yet. Earnings are held through the next calendar month,
                                        then paid out manually.
                                    </p>
                                </div>
                            </div>
                        ) : null}
                            </>
                        )}

                        {!statusLoading && !payoutReady && (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div className="layout-box widget-type2">
                                    <div className="widget-header">
                                        <div className="left-div">
                                            <h2>{PAYMENT_SETUP_ENABLED ? 'Payment setup required' : 'Payouts'}</h2>
                                            <h4>
                                                {PAYMENT_SETUP_ENABLED
                                                    ? summary?.held_balance > 0 || summary?.available_to_pay > 0
                                                        ? `You've earned $${formatMoney((summary.held_balance || 0) + (summary.available_to_pay || 0))} on the ledger. Finish payment setup when payouts open.`
                                                        : 'Finish payment setup to receive transfers when payouts open.'
                                                    : summary?.held_balance > 0 || summary?.available_to_pay > 0
                                                        ? `You've earned $${formatMoney(summary.lifetime_earnings)}. Payouts are processed manually after the calendar-month hold.`
                                                        : 'Your payouts will show up here after the hold, once ops pays out.'}
                                            </h4>
                                        </div>
                                    </div>
                                    {PAYMENT_SETUP_ENABLED && (
                                        <div className="widget-body" style={{ padding: 16 }}>
                                            <button
                                                type="button"
                                                className="action-btn"
                                                onClick={() => navigate('/user/payment-setup')}
                                            >
                                                <span className="text">Go to Payment Setup</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {payoutReady && (
                            <div className="layout-item" style={{ width: '100%' }}>
                                <div className="layout-box widget-type2">
                                    <div className="widget-header">
                                        <div className="left-div">
                                            <h2>Payouts</h2>
                                            <h4>
                                                Paid to {bankStatus.bank_name} ••••
                                                {bankStatus.account_number_last4} after the calendar-month hold.
                                            </h4>
                                        </div>
                                    </div>
                                    <div className="widget-body" style={{ padding: 16 }}>
                                        <button
                                            type="button"
                                            className="action-btn"
                                            onClick={() => navigate('/user/payment-setup')}
                                        >
                                            <span className="text">Manage bank account</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
